import Vue from "vue";
import {Constructor} from "../../global/constructor";
import {ArrayHelper} from "~/helpers/arrayHelper";
import {Hash} from "~/interfaces/hashInterfaces";
import {
  ICertificateElementContainer,
  ISortableCertificateElement,
} from "~/interfaces/contentManagementInterfaces";
import ElementHandler from "~/services/elementHandler";
import {ObjectHelper} from "~/helpers/objectHelper";
import TrackedModel from "~/models/tracked/trackedModel";
import CertificateElementHandler from "~/services/certificateElementHandler";
import DynamicModeCertificateElement from "./models/dynamicCertificateElement";
import AdaptiveLayoutSection from "./models/adaptiveLayoutSection";

interface ICertificateElementable<TCertificateElement> {
  rootModel: TrackedModel;
  certificate_elements: Hash<TCertificateElement>;
  _certificateElementHandler: ElementHandler;
  _classKey: string;
}

export default function CertificateElementable<TBase extends Constructor,
  TCertificateElement extends ISortableCertificateElement &
    TrackedModel,
  TContainer extends TrackedModel &
    ICertificateElementContainer<TCertificateElement> &
    TCertificateElement>(Base: TBase) {
  return class extends Base implements ICertificateElementable<TCertificateElement> {
    rootModel: TrackedModel;
    certificate_elements: Hash<TCertificateElement> = {};
    _certificateElementHandler: CertificateElementHandler;
    _classKey: string;

    get classKey(): string {
      return this._classKey;
    }

    get elementHandler(): ElementHandler {
      return this.certificateElementHandler;
    }

    get certificateElementHandler(): ElementHandler {
      return this._certificateElementHandler;
    }

    get certificateElementsArray(): TCertificateElement[] {
      return ObjectHelper.toArray(this.certificate_elements)
        .sort((a, b) => a.sort_order - b.sort_order);
    }

    get certificateElementsArrayDeep(): TCertificateElement[] {
      const elements = [];
      this.forEachCertificateElement(el => elements.push(el), true);
      return elements;
    }

    forEachCertificateElement(callback: (el: TCertificateElement) => void, recurse: boolean = false): void {
      for (const key in this.certificate_elements) {
        callback(this.certificate_elements[key]);
        if (recurse) {
          this.certificate_elements[key].forEachCertificateElement(callback, recurse);
        }
      }
    }
    get hasCertificateElements(): boolean {
      return this.certificateElementsArray.length > 0;
    }

    get lastContentElementInSortOrder(): TCertificateElement {
      return ArrayHelper.last(this.certificateElementsArray);
    }

    initializeEmbedded(parent: TContainer | TCertificateElement | DynamicModeCertificateElement | AdaptiveLayoutSection, item: TCertificateElement , position: number, json: object = {}, selectItem: boolean = true) {
      parent.trackCreationDeletion(item.client_id, null, item.asJson(), item);
      item.addToRepo();
      if (selectItem) {
        item.select();
      }

      Vue.set(parent[item.rootName], item.client_id, item);
      return item;
    }
  }
}